import { useRef } from 'react';

import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';
import { SHOULD_SHOW_CREATION_FOCUSED_NAV_SPOTLIGHT } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useSitePersonalizationQuery } from '@confluence/onboarding-helpers/entry-points/hooks/useSitePersonalizationQuery';
import { useSessionData } from '@confluence/session-data';

type UseCfind4473CreationFocusedNavResult = {
	isSpotlightVisible: boolean;
	areBannersHidden: boolean;
	isSimplifiedNav: boolean;
	onSpotlightShown?: () => void;
};

export const useCfind4473CreationFocusedNav = (
	updateState: boolean = false,
): UseCfind4473CreationFocusedNavResult => {
	const { setOnboardingState } = useSetOnboardingState();
	const { cloudId } = useSessionData();
	const { cohort, delayInMinutes } = getExperimentData();
	const hasUpdatedState = useRef(false);
	const isInExperimentControl = cohort !== 'treatment';

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(SHOULD_SHOW_CREATION_FOCUSED_NAV_SPOTLIGHT, isInExperimentControl);

	const {
		attributes: sitePersonalizationAttributes,
		loading: sitePersonalizationLoading,
		error: sitePersonalizationError,
	} = useSitePersonalizationQuery({
		cloudId,
		skip: isInExperimentControl,
	});

	const spotlightShown = () => {
		setOnboardingState({
			key: SHOULD_SHOW_CREATION_FOCUSED_NAV_SPOTLIGHT,
			value: 'false',
		});
	};

	if (isInExperimentControl) {
		return { isSpotlightVisible: false, areBannersHidden: false, isSimplifiedNav: false };
	}

	if (sitePersonalizationLoading || onboardingStateLoading) {
		// Simplify nav and hide banners while loading, to minimize flashing
		return { isSpotlightVisible: false, areBannersHidden: true, isSimplifiedNav: true };
	}

	if (sitePersonalizationError || onboardingStateError) {
		// Control behavior if there are any errors
		return { isSpotlightVisible: false, areBannersHidden: false, isSimplifiedNav: false };
	}

	const siteActivationDate = sitePersonalizationAttributes?.find(
		({ name }: { name: string }) => name === 'confluence.ondemand.activationDate',
	)?.value as string | undefined;

	const { shouldShowCreationFocusedNavSpotlight } = deserializeState(onboardingStateData);
	if (isSiteOlderThanDelay(delayInMinutes, siteActivationDate)) {
		if (shouldShowCreationFocusedNavSpotlight) {
			// Spotlight ready to show --> Show spotlight, bring nav features back, but don't bring banners back
			return {
				isSpotlightVisible: true,
				areBannersHidden: true,
				isSimplifiedNav: false,
				onSpotlightShown: spotlightShown,
			};
		}
		// Spotlight has already shown --> Don't show spotlight, bring nav features back, but don't bring banners back
		return { isSpotlightVisible: false, areBannersHidden: true, isSimplifiedNav: false };
	}

	// D0 site + user is in treatment --> set spotlight to show later, hide banners, and simplify nav
	else {
		if (
			shouldShowCreationFocusedNavSpotlight === undefined &&
			updateState &&
			!hasUpdatedState.current
		) {
			hasUpdatedState.current = true;
			setOnboardingState({
				key: SHOULD_SHOW_CREATION_FOCUSED_NAV_SPOTLIGHT,
				value: 'true',
			});
		}
	}

	return {
		isSpotlightVisible: false,
		areBannersHidden: true,
		isSimplifiedNav: true,
	};
};

type Cohort = 'control' | 'treatment';
type ExperimentData = {
	cohort: Cohort;
	delayInMinutes: number;
};

const getExperimentData = (): ExperimentData => {
	// eslint-disable-next-line confluence-feature-gating/unsafe-no-exposure
	const [expConfig] = UNSAFE_noExposureExp('cfind-4473-creation-focused-nav'); // Experiment exposure is fired in the backend
	const cohort = expConfig.get('cohort', 'control');
	const minutesPerHour = 60;
	const hoursPerDay = 24;
	const defaultDelayInMinutes = minutesPerHour * hoursPerDay;
	const delayInMinutes = expConfig.get('delayInMinutes', defaultDelayInMinutes.toString());
	return { cohort: cohort as Cohort, delayInMinutes: parseInt(delayInMinutes) };
};

const isSiteOlderThanDelay = (
	delayInMinutes: number,
	siteActivationDateString?: string,
): boolean => {
	const siteActivationDate = new Date(siteActivationDateString ?? '');
	if (isNaN(siteActivationDate.getTime())) {
		return false;
	}
	const createdAtTimestamp = siteActivationDate.getTime();
	const millisecondsSinceCreation = Date.now() - createdAtTimestamp;
	const secondsPerMinute = 60;
	const millisecondsPerSecond = 1000;
	const millisecondsInDelayTime = delayInMinutes * secondsPerMinute * millisecondsPerSecond;
	return millisecondsSinceCreation > millisecondsInDelayTime;
};
